import './TrafficMonitoringDetail.css';
import React, { useMemo, useState } from 'react';
import { DUPLICATE_ROUTE_ID, DUPLICATE_ROUTE_NAME, MSG_FILE_EXTENSION_INVALID, MSG_FILE_FORMAT_INVALID, TRAFFIC_MONITORING_PROPERTIES } from './constants';
import ImportFile from '../../components/ImportFile';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DetailHeader from 'components/DetailHeader';
import { MSG_CREATE_SUCCESS, TRAFFIC_MONITORING_TITLE } from 'pages/TrafficMonitoring/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { TEXT_BUTTON_UPDATE, TEXT_CANCEL } from 'constants/message';
import DirectionSetting from './componments/DirectionSetting';
import FanLoadingIcon from 'components/FanLoadingIcon';
import { apiCheckDuplicateRouteId, apiCheckDuplicateRouteName, apiDeleteTrafficMonitoring } from 'apis/TrafficMonitoringApi';
import { useDialog } from 'context/DialogContext';
import { useSensors } from 'context/SensorsContext';
import { useSensorGroups } from 'context/SensorGroupsContext';
import { useAuth } from 'context/AuthContext';
import { CAMERA_SETTING_INITIAL, checkAndRetrieveInfoFromForm, checkFileFormat, handleCreateTrafficMonitoring, handleUpdateTrafficMonitoring } from './helpers';
import { AccountType } from 'models';

function TrafficMonitoringDetail() {
    const navigate = useNavigate();
    const location = useLocation();
    const routeData = location.state?.routeData;
    const [isLoading, setIsLoading] = useState(false);
    const [trafficDirections, setTrafficDirections] = useState(routeData?.trafficDirections || [{ directionName: '', cameraSettings: [{...CAMERA_SETTING_INITIAL}] }]);
    const [uploadedFile, setUploadedFile] = useState(null);
    const { error: errorDialog, warningConfirm, success } = useDialog();
    const { currentUser } = useAuth();
    const isAdmin = useMemo(() => [AccountType.ADMIN, AccountType.MANAGER].includes(currentUser?.accountType), [currentUser]);


    const handleFileChange = async (file) => {
        try {
            if (!file) {
                setUploadedFile(null);
                return;
            }
            // Check file extension
            const extension = file.name.split('.').pop().toLowerCase();
            if (extension !== 'geojson') {
                setUploadedFile(null);
                errorDialog(MSG_FILE_EXTENSION_INVALID);
                return;
            }
            // Check file format
            const checkFile = await checkFileFormat(file);
            if(!checkFile) {
                setUploadedFile(null);
                errorDialog(MSG_FILE_FORMAT_INVALID);
                return;
            }
            setUploadedFile(file);
        } catch (error) {
            setUploadedFile(null);
            console.error('Error handling file change:', error);
        }
    };

    const onSubmit = async (e) => {
        try {
            e.preventDefault();

            // Retrieve info from form
            const routeDataKey = uploadedFile?.name || routeData?.routeDataKey;
            const {data, error} = checkAndRetrieveInfoFromForm(e, routeDataKey, trafficDirections);
            if (error) {
                errorDialog(error);
                return;
            }
            setIsLoading(true);

            let result = null;
            if(routeData?.routeId) {
                const isIdChanged = data.routeId !== routeData.routeId;
                if (isIdChanged) {
                    const duplicate = await apiCheckDuplicateRouteId(data.routeId);
                    if (duplicate.isExisted) {
                        errorDialog(DUPLICATE_ROUTE_ID);
                        setIsLoading(false); 
                        return;
                    }
                }

                if (data.routeName !== routeData.routeName) {
                    const duplicateName = await apiCheckDuplicateRouteName(data.routeName);
                    if (duplicateName.isExisted) {
                        setIsLoading(false); 
                        warningConfirm(
                            DUPLICATE_ROUTE_NAME,
                            async () =>{
                                try {
                                    setIsLoading(true);
                                    await performUpdateOrMigration(isIdChanged, routeData.routeId, data, uploadedFile);
                                } catch (error) {
                                    errorDialog('エラーが発生しました。もう一度お試しください。');
                                    console.error('Error submitting form:', error);
                                } finally {
                                    setIsLoading(false);
                                }
                            },
                            {confirmText: 'OK', cancelText: 'キャンセル'}
                        );
                        return;
                    }
                }
                
                await performUpdateOrMigration(isIdChanged, routeData.routeId, data, uploadedFile);

            } else {
                const duplicate = await apiCheckDuplicateRouteId(data.routeId);
                if (duplicate.isExisted) {
                    errorDialog(DUPLICATE_ROUTE_ID);
                    setIsLoading(false);
                    return;
                }
                const duplicateName = await apiCheckDuplicateRouteName(data.routeName);
                if (duplicateName.isExisted) {
                    setIsLoading(false);
                    warningConfirm(
                        DUPLICATE_ROUTE_NAME,
                        async () =>{
                            try {
                                setIsLoading(true);
                                const result = await handleCreateTrafficMonitoring(data, uploadedFile);
                                if (result.success) {
                                    success(MSG_CREATE_SUCCESS);
                                    navigate(-1);
                                }
                            } catch (error) {
                                errorDialog('エラーが発生しました。もう一度お試しください。');
                                console.error('Error submitting form:', error);
                            } finally {
                                setIsLoading(false);
                            }
                        },
                        {confirmText: 'OK', cancelText: 'キャンセル'}
                    );
                    return;
                }

                result = await handleCreateTrafficMonitoring(data, uploadedFile);
                if (result.success) {
                    success(MSG_CREATE_SUCCESS);
                    navigate(-1);
                }
            }
        } catch (error) {
            errorDialog('エラーが発生しました。もう一度お試しください。');
            console.error('Error submitting form:', error);
        } finally {
            if(!isLoading) setIsLoading(false);
        }
    }

    const performUpdateOrMigration = async (isIdChanged, oldRouteId, newData, file) => {
        let result = null;
        
        if (isIdChanged) {
            const createResult = await handleCreateTrafficMonitoring(newData, file);
            
            if (createResult.success) {
                try {
                    if (apiDeleteTrafficMonitoring) {
                        await apiDeleteTrafficMonitoring(oldRouteId);
                    } else {
                        console.warn("There is no delete API yet, old data still exists" + oldRouteId);
                    }
                    result = { success: true };
                } catch (deleteError) {
                    console.error("Error when deleting old record", deleteError);
                    result = { success: true };
                }
            } else {
                throw new Error("Cannot create a new record with a changed ID");
            }
        } else {
            result = await handleUpdateTrafficMonitoring(oldRouteId, newData, file);
        }

        if (result && result.success) {
            success(MSG_CREATE_SUCCESS);
            navigate(-1);
        }
    }

    const onAddDirection = () => {
        if (trafficDirections.length < 2) {
            setTrafficDirections(prev => ([...prev, {
                directionName: '',
                cameraSettings: [{...CAMERA_SETTING_INITIAL, no: '1'}] 
            }]));
        }
    };

    const onRemoveDirection = (index) => {
        setTrafficDirections(prev => prev.filter((_, i) => i !== index))
    };

    const renderDirectionSetting = () => {
        if (!trafficDirections || trafficDirections.length === 0) return null;
        if (trafficDirections.length === 1)
            return <>
                <DirectionSetting key={`dir-1`}
                    directionIndex={0}
                    directionName={trafficDirections[0].directionName}
                    cameraSettings={trafficDirections[0].cameraSettings}
                    isEditable={isAdmin}
                />
                <button className='tmd__add-direction-btn'
                    onClick={onAddDirection} title='Add Direction'>
                        <FontAwesomeIcon icon={faPlus} />
                </button>
            </>
        if (trafficDirections.length === 2)
            return <>
                <DirectionSetting key={`dir-1`}
                    directionIndex={0}
                    directionName={trafficDirections[0].directionName}
                    cameraSettings={trafficDirections[0].cameraSettings}
                    isEditable={isAdmin}
                />
                <DirectionSetting key={`dir-2`}
                    directionIndex={1}
                    directionName={trafficDirections[1].directionName}
                    cameraSettings={trafficDirections[1].cameraSettings}
                    onRemoveDirection={onRemoveDirection}
                    isEditable={isAdmin}
                />
            </>
    };

    return (
        <div id='traffic-monitoring-detail'>
            {isLoading ? <FanLoadingIcon size={80} position='fixed' zIndex={9999} /> : null}
            <DetailHeader
                mainLabel={TRAFFIC_MONITORING_TITLE}
                subLabel={routeData ? routeData.routeName : '新規作成'}
                onClick={() => { navigate(-1); }}
            />
            <form className='tmd__container' onSubmit={onSubmit}>
                <p className='tmd__heading'>（路線）</p>
                <section className='tmd__route-section'>
                    <section className="tmd__form-section">
                        <div className="tmd__label">
                            {TRAFFIC_MONITORING_PROPERTIES.NO}
                            <label className="tmd__required">*</label>
                        </div>
                        
                        <div className='tmd__input-container'>
                            <input
                                id='routeId'
                                className={`tmd__input`}
                                type='text'
                                name='routeId'
                                defaultValue={routeData?.routeId}
                                disabled={!isAdmin}
                            />
                        </div>
                    </section>
                    <section className="tmd__form-section">
                        <div className="tmd__label">
                            {TRAFFIC_MONITORING_PROPERTIES.NAME}
                            <label className="tmd__required">*</label>
                        </div>
                        <div className='tmd__input-container'>
                            <input
                                className={'tmd__input'}
                                type='text'
                                name='routeName'
                                defaultValue={routeData?.routeName}
                                disabled={!isAdmin}
                            />
                        </div>
                    </section>
                    <section className="tmd__form-section">
                        <div className="tmd__label">
                            {TRAFFIC_MONITORING_PROPERTIES.ROUTE_DATA}
                            <label className="tmd__required">*</label>
                        </div>
                        <div className="tmd__import-file">
                            <ImportFile
                                disabled={!isAdmin}
                                filename={routeData?.routeDataKey}
                                onChangeFile={handleFileChange}
                                accept={'.geojson'}
                                selectedFile={uploadedFile}
                            />
                        </div>
                    </section>
                </section>
                {renderDirectionSetting()}
                <section className="tmd__buttons-container">
                    {isAdmin && <button className="tmd__button button_active" type='submit'>
                        {TEXT_BUTTON_UPDATE}
                    </button>}
                    <button className="tmd__button button_cancel" onClick={() => navigate('/traffic-monitoring')}>
                        {TEXT_CANCEL}
                    </button>
                </section>
            </form>
        </div>
    );
}

export default TrafficMonitoringDetail;